import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { HaulWareConnection } from '~hooks/useHaulWare/models';
import useHaulWare, {
  HaulWareConnectionUpdateInput,
  readRejection,
} from '~hooks/useHaulWare/useHaulWare';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import HaulWareConnectionForm, {
  HaulWareConnectionFormHandler,
} from '~pages/Settings/Integrations/HaulWareConnectionForm';
import HaulWareSetupForm, {
  HaulWareSetupFormHandler,
} from '~pages/Settings/Integrations/HaulWareSetupForm';
import { ROUTES as routes } from '~router/routePaths';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

const HaulWareManagement = () => (
  <HaulWareManagementAccess component={HaulWareManagementPage} />
);

const HaulWareManagementPage = observer(() => {
  const { toasterStore, userStore } = useStores();
  const { createConnection, deleteConnection, getConnection, updateConnection } =
    useHaulWare();

  const companyId = getEffectiveUserCompanyId(userStore);
  const canDelete = userStore.getPermissions().canDeleteHaulWareIntegration;

  const [isLoading, setIsLoading] = useState(true);
  const [didLoadFail, setDidLoadFail] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
  const [pendingAppCodeUpdate, setPendingAppCodeUpdate] =
    useState<HaulWareConnectionUpdateInput | null>(null);
  const [haulWareConnection, setHaulWareConnection] = useState<HaulWareConnection | null>(
    null,
  );

  const connectionFormRef = useRef<HaulWareConnectionFormHandler>(null);
  const setupFormRef = useRef<HaulWareSetupFormHandler>(null);
  // Two clicks in the same tick both read the pre-commit value of the state flags below,
  // so only a ref keeps a second request from starting.
  const isMutatingRef = useRef(false);

  useEffect(() => {
    let isCurrentCompany = true;

    const loadConnection = async () => {
      setIsLoading(true);
      try {
        const loaded = await getConnection(companyId);
        if (!isCurrentCompany) return;

        setHaulWareConnection(loaded);
        setDidLoadFail(false);
      } catch {
        if (!isCurrentCompany) return;

        setDidLoadFail(true);
      } finally {
        if (isCurrentCompany) setIsLoading(false);
      }
    };

    void loadConnection();

    return () => {
      isCurrentCompany = false;
    };
  }, [companyId, getConnection]);

  const isBusy = isSaving || isConnecting || isDisconnecting;
  const isBlockedByAnotherAction = isBusy && !isSaving;

  const handleConnectClick = useCallback(async () => {
    if (isMutatingRef.current) return;
    isMutatingRef.current = true;

    setIsConnecting(true);
    try {
      const formData = await setupFormRef.current?.onSubmit();
      if (!formData) return;

      const created = await createConnection(companyId, formData);
      if (!created) return;

      setHaulWareConnection(created);
      toasterStore.push(
        alert(
          t('administration.integration.haul_ware.toast.connected'),
          AlertTypes.success,
        ),
      );
    } catch (error) {
      const { field, message } = readRejection(error);
      setupFormRef.current?.setFieldError(field, message);
    } finally {
      isMutatingRef.current = false;
      setIsConnecting(false);
    }
  }, [companyId, createConnection, toasterStore]);

  const saveConnection = useCallback(
    async (input: HaulWareConnectionUpdateInput) => {
      if (isMutatingRef.current) return;

      isMutatingRef.current = true;
      setIsSaving(true);
      try {
        const updated = await updateConnection(companyId, input);
        if (!updated) return;

        setHaulWareConnection(updated);
        toasterStore.push(
          alert(
            t('administration.integration.haul_ware.toast.saved'),
            AlertTypes.success,
          ),
        );
      } catch (error) {
        const { field, message } = readRejection(error);
        connectionFormRef.current?.setFieldError(
          field === 'apiUser' ? 'appCode' : field,
          message,
        );
      } finally {
        isMutatingRef.current = false;
        setIsSaving(false);
      }
    },
    [companyId, updateConnection, toasterStore],
  );

  const handleSaveClick = useCallback(async () => {
    const formData = await connectionFormRef.current?.onSubmit();
    if (!formData || !haulWareConnection) return;

    const isAppCodeChanged = formData.appCode !== haulWareConnection.appCode;
    const input: HaulWareConnectionUpdateInput = {
      enabled: formData.enabled,
      ...(isAppCodeChanged ? { appCode: formData.appCode } : {}),
      ...(formData.apiPassword ? { apiPassword: formData.apiPassword } : {}),
    };

    if (isAppCodeChanged) {
      setPendingAppCodeUpdate(input);
      return;
    }

    await saveConnection(input);
  }, [haulWareConnection, saveConnection]);

  const handleAppCodeConfirm = useCallback(
    async (input: HaulWareConnectionUpdateInput) => {
      try {
        await saveConnection(input);
      } finally {
        setPendingAppCodeUpdate(null);
      }
    },
    [saveConnection],
  );

  const handleDisconnectConfirm = useCallback(async () => {
    if (isMutatingRef.current) return;

    isMutatingRef.current = true;
    setIsDisconnecting(true);
    try {
      const deleted = await deleteConnection(companyId);
      if (!deleted) return;

      setHaulWareConnection(null);
      setIsDisconnectDialogOpen(false);
      toasterStore.push(
        alert(
          t('administration.integration.haul_ware.toast.disconnected'),
          AlertTypes.success,
        ),
      );
    } finally {
      isMutatingRef.current = false;
      setIsDisconnecting(false);
    }
  }, [companyId, deleteConnection, toasterStore]);

  const handleDisconnectDialogClose = useCallback(() => {
    if (isDisconnecting) return;

    setIsDisconnectDialogOpen(false);
  }, [isDisconnecting]);

  const handleAppCodeDialogClose = useCallback(() => {
    if (isSaving) return;

    setPendingAppCodeUpdate(null);
  }, [isSaving]);

  const renderConnection = () => {
    if (didLoadFail) {
      return (
        <Alert severity="error" data-test-id="haul-ware-load-error">
          {t('error_messages.haul_ware.failed_to_fetch_connection')}
        </Alert>
      );
    }

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!haulWareConnection) {
      return (
        <>
          <Typography sx={{ color: 'text.secondary', mb: 3 }}>
            {t('administration.integration.haul_ware.setup_description')}
          </Typography>

          <HaulWareSetupForm isDisabled={isBusy} ref={setupFormRef} />

          <Box sx={{ display: 'flex', mt: 3 }}>
            <SmallLoadingButton
              color="brandV2Yellow"
              loading={isConnecting}
              onClick={handleConnectClick}
            >
              {t('administration.integration.haul_ware.cta.connect')}
            </SmallLoadingButton>
          </Box>
        </>
      );
    }

    return (
      <>
        <HaulWareConnectionForm
          haulWareConnection={haulWareConnection}
          isDisabled={isBusy}
          // Re-seeds the form after a save so a changed password is not resent.
          key={haulWareConnection.updatedAt.valueOf()}
          ref={connectionFormRef}
        />

        <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
          <SmallLoadingButton
            color="brandV2Yellow"
            disabled={isBlockedByAnotherAction}
            loading={isSaving}
            onClick={handleSaveClick}
          >
            {t('actions.save')}
          </SmallLoadingButton>

          {canDelete ? (
            <SmallButton
              color="brandV2Black"
              disabled={isBusy}
              onClick={() => setIsDisconnectDialogOpen(true)}
              sx={{ borderColor: 'divider', color: 'text.primary' }}
              variant="outlined"
            >
              {t('administration.integration.haul_ware.cta.disconnect')}
            </SmallButton>
          ) : null}
        </Box>
      </>
    );
  };

  return (
    <Box sx={{ maxWidth: '1000px' }}>
      <Button
        color="secondary"
        component={Link}
        startIcon={<ArrowBackIosNew fontSize="small" />}
        to={`/${routes.settings.base}/${routes.settings.integrations.base}`}
        variant="text"
      >
        {t('administration.integration.title')}
      </Button>
      <SettingsPageHeader
        title={t('administration.integration.haul_ware.name')}
        description={t('administration.integration.haul_ware.description')}
      />

      {renderConnection()}

      {pendingAppCodeUpdate !== null ? (
        <Dialog isOpen maxWidth="sm" onClose={handleAppCodeDialogClose}>
          <DialogHeader onClose={handleAppCodeDialogClose}>
            {t('administration.integration.haul_ware.app_code_confirmation_title')}
          </DialogHeader>
          <DialogContent>
            <Typography>
              {t('administration.integration.haul_ware.app_code_confirmation_body')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <SmallButton
              color="secondary"
              disabled={isSaving}
              onClick={handleAppCodeDialogClose}
              variant="outlined"
            >
              {t('actions.cancel')}
            </SmallButton>
            <SmallLoadingButton
              color="brandV2Yellow"
              loading={isSaving}
              onClick={() => handleAppCodeConfirm(pendingAppCodeUpdate)}
            >
              {t('administration.integration.haul_ware.cta.confirm_app_code')}
            </SmallLoadingButton>
          </DialogActions>
        </Dialog>
      ) : null}

      <ConfirmationDialog
        confirmButtonProps={{
          children: t('administration.integration.haul_ware.cta.disconnect'),
        }}
        isBusy={isDisconnecting}
        isOpen={isDisconnectDialogOpen}
        onClose={handleDisconnectDialogClose}
        onConfirm={handleDisconnectConfirm}
        title={t('administration.integration.haul_ware.disconnect_confirmation_title')}
      >
        <Typography>
          {t('administration.integration.haul_ware.disconnect_confirmation_body')}
        </Typography>
      </ConfirmationDialog>
    </Box>
  );
});

interface HaulWareManagementAccessProps {
  component: React.ComponentType;
}

const HaulWareManagementAccess = observer(
  ({ component: Component }: HaulWareManagementAccessProps) => {
    const { userStore } = useStores();
    const navigate = useNavigate();

    const userCanManageHaulWareIntegration =
      userStore.getPermissions().canManageHaulWareIntegration;

    if (!userCanManageHaulWareIntegration) {
      navigate(routes.base);
      return null;
    }

    return <Component />;
  },
);

export default HaulWareManagement;
